import { useState, useRef, useEffect, Component, ReactNode } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { usePatientData } from "@/hooks/usePatientData";
import { PatientPageHeader } from "@/components/patient/PatientPageHeader";
import { PatientIndicadores } from "@/components/patient/PatientIndicadores";
import { PatientStatistics } from "@/components/patient/PatientStatistics";
import { PatientHealthRecords } from "@/components/patient/PatientHealthRecords";
import { PatientSmartfileRecords } from "@/components/patient/PatientSmartfileRecords";
import { PatientTimeline } from "@/components/patient/PatientTimeline";
import { AssistedCollectionTab } from "@/components/patient/AssistedCollectionTab";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

import { DocumentAnalysisModal } from "@/components/patient/DocumentAnalysisModal";
import PatientResultados from "@/components/patient/PatientResultados";
import { GlobalAnalysisModern } from "@/components/patient/GlobalAnalysisModern";
import { PatientVision360 } from "@/components/patient/vision360";
import { LabResultsReport } from "@/components/patient/LabResultsReport";
import { PatientGallery } from "@/components/patient/PatientGallery";
import {
  PatientDocumentUpload,
  ProcessedPatientDocument,
} from "@/components/patient/PatientDocumentUpload";
import { PatientDocumentValidation } from "@/components/patient/PatientDocumentValidation";
import PatientNoteInput from "@/components/patient/PatientNoteInput";
import { CameraCaptureMulti } from "@/components/smartfile/CameraCaptureMulti";
import { AudioOverlay } from "@/components/AudioOverlay";
import { TextInputDialog } from "@/components/TextInputDialog";
import { PDFExportLoader } from "@/components/patient/PDFExportLoader";
import { PatientIndicadorsPDFContent } from "@/components/patient/PatientIndicadorsPDFContent";
import html2pdf from "html2pdf.js";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import { ArrowLeft, FileBarChart, Share2, Loader2, ExternalLink, Settings2, Upload, FileCheck, Clock, AlertCircle, BarChart3 } from "lucide-react";
import { useGenerateShareLink } from "@/hooks/useGenerateShareLink";
import { ShareBlockSelector, DEFAULT_SHARE_BLOCKS, type ShareBlocksState } from "@/components/patient/ShareBlockSelector";
import { useLinkByName } from "@/hooks/useLinkByName";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { createSmartfileSession, getSmartfileSession, finalizeSmartfileSession } from "@/services/smartfile";
import { processFileUploadUltraFast, processTextInputUltraFast } from "@/services/smartfile-fast";
import { saveStructuredRecords } from "@/services/miafile.records";
import { Progress } from "@/components/ui/progress";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { UpgradeModal } from "@/components/subscription/UpgradeModal";
import { isAIDisabled } from "@/lib/smartfile-ai-preference";

// ErrorBoundary para capturar erros de renderização
class PatientDetailsErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    console.error("❌ Erro no componente PacienteDetalhes:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 space-y-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Erro ao carregar página do paciente</AlertTitle>
            <AlertDescription>
              Ocorreu um erro ao renderizar a página do paciente.
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer">Ver detalhes técnicos</summary>
                <pre className="mt-2 p-2 bg-muted rounded overflow-auto">
                  {this.state.error?.message}
                  {'\n\n'}
                  {this.state.error?.stack}
                </pre>
              </details>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function PacienteDetalhes() {
  const { id } = useParams();
  console.log("🔍 PacienteDetalhes montado, patientId:", id);
  const navigate = useNavigate();
  const location = useLocation();
  const isPatientArea = location.pathname.startsWith("/paciente");
  const basePath: "/app" | "/paciente" = isPatientArea ? "/paciente" : "/app";
  const availableTabs = isPatientArea
    ? ["timeline", "indicators", "vision360", "registros", "galeria"]
    : undefined;
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("timeline");
  const [accordionValue, setAccordionValue] = useState<string>("");
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<'smartfile' | 'document'>('smartfile');
  const [processedDocuments, setProcessedDocuments] = useState<
    ProcessedPatientDocument[]
  >([]);
  const [showCamera, setShowCamera] = useState(false);
  const [showAudio, setShowAudio] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressStage, setProgressStage] = useState<string>("");
  const [progressValue, setProgressValue] = useState<number>(0);
  const [captureSectionOpen, setCaptureSectionOpen] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // ✅ Upload múltiplo - Jobs em background
  const [uploadJobs, setUploadJobs] = useState<Map<string, {
    fileName: string;
    status: 'pending' | 'processing' | 'saving' | 'completed' | 'failed';
    progress: number;
    stage: string;
    sessionId?: string;
    toastId?: string | number;
    error?: string;
  }>>(new Map());
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const [lastGeneratedShareUrl, setLastGeneratedShareUrl] = useState<string | null>(null);
  const [professionalProfile, setProfessionalProfile] = useState<{
    full_name: string;
  } | null>(null);
  const [isWhatsAppSharing, setIsWhatsAppSharing] = useState(false);
  const [selectedBlocks, setSelectedBlocks] = useState<ShareBlocksState>(DEFAULT_SHARE_BLOCKS);
  const [showBlockSelector, setShowBlockSelector] = useState(false);
  
  // Verificar status da assinatura para limitação de upload
  const { canUploadMultipleFiles, getMaxFileUploads, currentPlan } = useSubscriptionStore();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [requestedFileCount, setRequestedFileCount] = useState(0);

  // Buscar perfil do profissional
  useEffect(() => {
    const loadProfessionalProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user.id)
          .single();
        setProfessionalProfile(data);
      }
    };
    loadProfessionalProfile();
  }, []);

  // Custom hook que encapsula toda lógica de fetch
  const {
    patient,
    appointments,
    documents,
    labResults,
    insights,
    registrosSaude,
    alerts,
    subcategories,
    globalAnalysisCache,
    smartfileSessions,
    trainingPlans,
    loading,
    loadingData,
    refetch,
    setGlobalAnalysisCache,
  } = usePatientData(id || undefined);

  // Atualizar título da aba com nome do paciente
  useEffect(() => {
    if (patient?.name) {
      document.title = `elinia - ${patient.name}`;
    }
    return () => {
      document.title = "elinia - Saúde com Inteligência";
    };
  }, [patient?.name]);

  // Log de acesso ao paciente
  useEffect(() => {
    const logAccess = async () => {
      if (!id) return;
      try {
        // Verificar se o usuário está autenticado antes de fazer a chamada
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        await supabase.functions.invoke('log-user-access', {
          body: { 
            action: 'view_patient', 
            entity: 'patient',
            entityId: id 
          }
        });
      } catch (error) {
        console.error('Error logging patient access:', error);
      }
    };
    logAccess();
  }, [id]);

  // Atualizar gráficos apenas quando todos os jobs forem concluídos
  useEffect(() => {
    const activeJobs = Array.from(uploadJobs.values()).filter(
      job => job.status === 'processing' || job.status === 'saving'
    );
    
    const completedJobs = Array.from(uploadJobs.values()).filter(
      job => job.status === 'completed'
    );

    // Se não há jobs ativos mas há jobs completados, atualizar uma vez
    if (activeJobs.length === 0 && completedJobs.length > 0 && id) {
      // Pequeno delay para garantir que tudo foi processado
      const timeoutId = setTimeout(() => {
        console.log('[Update] Todos os jobs concluídos, atualizando gráficos...');
        
        // Invalidar queries do React Query
        queryClient.invalidateQueries({ queryKey: ["lab-results", id] });
        queryClient.invalidateQueries({ queryKey: ["patient", id] });
        queryClient.invalidateQueries({ queryKey: ["lab-subcategories"] });
        
        // Atualizar dados do hook usePatientData
        refetch();
        
        // Disparar evento para atualizar gráficos
        window.dispatchEvent(new CustomEvent('reload-health-indicators'));
      }, 1000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [uploadJobs, refetch, id, queryClient]);
  
  console.log("🔍 Dados do hook:", {
    patientId: id,
    hasPatient: !!patient,
    labResultsCount: labResults?.length || 0,
    loadingLabResults: loadingData.labResults
  });

  // Scroll e abrir accordion para seção do relatório quando hash presente na URL
  useEffect(() => {
    if (location.hash === "#relatorio-analitico" && activeTab === "indicators" && !loading) {
      // Abrir o accordion
      setAccordionValue("relatorio-analitico");
      // Scroll suave após um pequeno delay para garantir que o accordion abriu
      setTimeout(() => {
        const element = document.getElementById("relatorio-analitico");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 400);
    }
  }, [location.hash, activeTab, loading]);

  useEffect(() => {
    if (isProcessing) {
      setCaptureSectionOpen(true);
    }
  }, [isProcessing]);

  const handleNoteCreated = (noteData: {
    documentId: string;
    fileName: string;
    extractedText: string;
  }) => {
    const processedNote: ProcessedPatientDocument = {
      documentId: noteData.documentId,
      appointmentId: "",
      fileName: noteData.fileName,
      fileType: "anotacao",
      filePath: "",
      extractedText: noteData.extractedText,
      lab_results: "",
      analysis: {
        summary: "",
        insights: [],
        risk_level: "baixo",
      },
    };

    setProcessedDocuments([processedNote]);
    setShowNoteDialog(false);
    setShowValidationDialog(true);
  };

  // Handlers para câmera, áudio e arquivo
  const handleCameraClick = () => {
    setShowCamera(true);
  };

  const handleAudioClick = () => {
    setShowAudio(true);
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleTextClick = () => {
    setShowTextInput(true);
  };

  const handlePhotoCapture = async (files: File[]) => {
    setShowCamera(false);
    
    // Verificar limite de upload múltiplo baseado no plano
    const maxUploads = currentPlan?.max_file_uploads ?? 1;
    
    // Se for apenas uma foto, usar fluxo normal (navega para análise)
    if (files.length === 1) {
      await processFile(files[0]);
      return;
    }

    // Verificar se o plano permite upload múltiplo
    if (maxUploads === 1) {
      toast.error("Seu plano não permite upload múltiplo. Envie um arquivo por vez.");
      return;
    }

    // Verificar limite máximo
    const maxAllowed = maxUploads || 10; // NULL = ilimitado, mas limitamos a 10 por segurança
    if (maxUploads && files.length > maxUploads) {
      toast.error(`Máximo de ${maxUploads} arquivo(s) por vez. Selecione até ${maxUploads} arquivo(s).`);
      return;
    }

    // Se for múltiplas fotos, processar em background
    if (files.length > 10) {
      toast.error("Máximo de 10 fotos por vez. Capture até 10 fotos.");
      return;
    }

    // Converter array de File para FileList simulada para usar processMultipleFiles
    const dataTransfer = new DataTransfer();
    files.forEach(file => dataTransfer.items.add(file));
    await processMultipleFiles(dataTransfer.files);
  };

  const handleAudioComplete = async (file: File) => {
    setShowAudio(false);
    await processFile(file);
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Se apenas um arquivo, usar fluxo antigo (navega para análise)
    if (files.length === 1) {
      const file = files[0];
      const validTypes = ["pdf", "jpg", "jpeg", "png", "xlsx", "xls", "csv", "mp3", "wav", "m4a", "webm"];
      const ext = file.name.split(".").pop()?.toLowerCase() || "";

      if (!validTypes.includes(ext)) {
        toast.error("Tipo de arquivo não suportado. Use PDF, imagens, planilhas ou áudio.");
        return;
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await processFile(file);
      return;
    }

    // Validar limite de upload múltiplo usando o store
    if (!canUploadMultipleFiles(files.length)) {
      const maxUploads = getMaxFileUploads();
      if (maxUploads === 1 || maxUploads === null) {
        // Plano não permite upload múltiplo - mostrar modal de upgrade
        setRequestedFileCount(files.length);
        setShowUpgradeModal(true);
      } else {
        toast.error(`Máximo de ${maxUploads} arquivo(s) por vez. Selecione até ${maxUploads} arquivo(s).`);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Múltiplos arquivos - processar em background
    if (files.length > 10) {
      toast.error("Máximo de 10 arquivos por vez. Selecione até 10 arquivos.");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    await processMultipleFiles(files);
  };

  const processFile = async (file: File) => {
    if (!patient) return;

    setIsProcessing(true);
    setProgressStage("Iniciando...");
    setProgressValue(0);

    try {
      // Criar sessão smartfile vinculada ao paciente
      setProgressStage("Criando sessão...");
      setProgressValue(5);
      const sessionId = await createSmartfileSession(patient.id);

      // Processar arquivo completo
      await processFileUploadUltraFast(sessionId, file, (stage, progress) => {
        setProgressStage(stage);
        setProgressValue(progress);
      }, { skipAIInsights: isAIDisabled() });

      // ✅ VALIDAÇÃO: Verificar se o documento é "other" após processamento
      const session = await getSmartfileSession(sessionId);
      if (session.content_type === 'other') {
        // Deletar arquivo do storage se existir
        if (session.file_path) {
          try {
            await supabase.storage
              .from('patient-docs')
              .remove([session.file_path]);
          } catch (storageError) {
            console.error('Erro ao deletar arquivo do storage:', storageError);
          }
        }
        
        // Deletar lab_results associados se existirem
        try {
          await supabase
            .from('lab_results_onboarding')
            .delete()
            .eq('source_session_id', sessionId);
        } catch (labError) {
          console.error('Erro ao deletar lab_results:', labError);
        }
        
        // Deletar a sessão completamente
        try {
          await supabase
            .from('smartfile_sessions')
            .delete()
            .eq('id', sessionId);
        } catch (sessionError) {
          console.error('Erro ao deletar sessão:', sessionError);
        }
        
        toast.error("O arquivo enviado não possui dados clínicos de saúde. Favor verificar.");
        setIsProcessing(false);
        setProgressStage("");
        setProgressValue(0);
        return;
      }

      setProgressStage("Concluído!");
      setProgressValue(100);
      toast.success("Documento processado com sucesso!");
      
      // Navegar para tela de análise inteligente (mesmo comportamento da página inicial)
      setTimeout(() => {
        navigate(`${basePath}/smartfile?sessionId=${sessionId}&patientId=${patient.id}`);
      }, 500);
    } catch (error) {
      console.error("Erro ao processar arquivo:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro ao processar documento";
      toast.error(errorMessage);
      setProgressStage("");
      setProgressValue(0);
    } finally {
      setIsProcessing(false);
    }
  };

  // ✅ Função para processar múltiplos arquivos em background
  const processMultipleFiles = async (files: FileList) => {
    if (!patient) return;

    const validTypes = ["pdf", "jpg", "jpeg", "png", "xlsx", "xls", "csv", "mp3", "wav", "m4a", "webm"];
    const maxFileSize = 50 * 1024 * 1024; // 50MB
    
    // Validar arquivos
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];
    
    Array.from(files).forEach(file => {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!validTypes.includes(ext)) {
        invalidFiles.push(`${file.name} (tipo não suportado)`);
        return;
      }
      if (file.size > maxFileSize) {
        invalidFiles.push(`${file.name} (muito grande: ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
        return;
      }
      validFiles.push(file);
    });
    
    if (invalidFiles.length > 0) {
      toast.error(`${invalidFiles.length} arquivo(s) inválido(s): ${invalidFiles.slice(0, 3).join(', ')}${invalidFiles.length > 3 ? '...' : ''}`);
    }
    
    if (validFiles.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }
    
    // Criar jobs para cada arquivo válido
    const newJobs = new Map(uploadJobs);
    const fileJobMap = new Map<File, string>();
    
    validFiles.forEach(file => {
      const jobId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const toastId = toast.loading(`Preparando ${file.name}...`, {
        description: 'Aguardando processamento'
      });
      
      newJobs.set(jobId, {
        fileName: file.name,
        status: 'pending',
        progress: 0,
        stage: 'Aguardando...',
        toastId
      });
      
      fileJobMap.set(file, jobId);
    });
    
    setUploadJobs(newJobs);
    
    // Processar cada arquivo em background
    validFiles.forEach((file) => {
      const jobId = fileJobMap.get(file);
      if (jobId) {
        processFileInBackground(file, jobId);
      }
    });
    
    // Limpar input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  
  // ✅ Processar um arquivo em background e salvar automaticamente
  const processFileInBackground = async (
    file: File,
    jobId: string
  ) => {
    if (!patient) return;

    // Buscar job atual do estado
    setUploadJobs(prevJobs => {
      const job = prevJobs.get(jobId);
      if (!job) return prevJobs;
      
      const updatedJob = {
        ...job,
        status: 'processing' as const,
        stage: 'Criando sessão...',
        progress: 5
      };
      
      const newJobs = new Map(prevJobs);
      newJobs.set(jobId, updatedJob);
      
      if (job.toastId) {
        toast.loading(`Processando ${file.name}...`, {
          id: job.toastId,
          description: 'Criando sessão...'
        });
      }
      
      return newJobs;
    });
    
    try {
      // Criar sessão smartfile vinculada ao paciente
      const sessionId = await createSmartfileSession(patient.id);
      
      setUploadJobs(prevJobs => {
        const job = prevJobs.get(jobId);
        if (!job) return prevJobs;
        
        const updatedJob = {
          ...job,
          sessionId,
          stage: 'Enviando arquivo...',
          progress: 10
        };
        
        const newJobs = new Map(prevJobs);
        newJobs.set(jobId, updatedJob);
        
        if (job.toastId) {
          toast.loading(`Processando ${file.name}...`, {
            id: job.toastId,
            description: 'Enviando arquivo...'
          });
        }
        
        return newJobs;
      });
      
      // Processar arquivo com callbacks de progresso
      await processFileUploadUltraFast(
        sessionId,
        file,
        (stage, progress) => {
          setUploadJobs(prevJobs => {
            const currentJob = prevJobs.get(jobId);
            if (!currentJob) return prevJobs;
            
            const updatedJob = {
              ...currentJob,
              stage,
              progress: Math.min(progress, 90) // Máximo 90% até salvar registros
            };
            
            const newJobs = new Map(prevJobs);
            newJobs.set(jobId, updatedJob);
            
            if (currentJob.toastId) {
              toast.loading(`Processando ${file.name}...`, {
                id: currentJob.toastId,
                description: `${stage} (${Math.min(progress, 90)}%)`
              });
            }
            
            return newJobs;
          });
        },
        { skipAIInsights: isAIDisabled() }
      );
      
      // Buscar sessão processada
      setUploadJobs(prevJobs => {
        const job = prevJobs.get(jobId);
        if (!job) return prevJobs;
        
        const updatedJob = {
          ...job,
          status: 'saving' as const,
          stage: 'Salvando registros...',
          progress: 92
        };
        
        const newJobs = new Map(prevJobs);
        newJobs.set(jobId, updatedJob);
        
        if (job.toastId) {
          toast.loading(`Processando ${file.name}...`, {
            id: job.toastId,
            description: 'Salvando registros...'
          });
        }
        
        return newJobs;
      });
      
      const session = await getSmartfileSession(sessionId);
      
      // ✅ VALIDAÇÃO: Se o documento for "other", não processar, deletar tudo e exibir toast
      if (session.content_type === 'other') {
        // Deletar arquivo do storage se existir
        if (session.file_path) {
          try {
            await supabase.storage
              .from('patient-docs')
              .remove([session.file_path]);
          } catch (storageError) {
            console.error('Erro ao deletar arquivo do storage:', storageError);
          }
        }
        
        // Deletar lab_results associados se existirem
        try {
          await supabase
            .from('lab_results_onboarding')
            .delete()
            .eq('source_session_id', sessionId);
        } catch (labError) {
          console.error('Erro ao deletar lab_results:', labError);
        }
        
        // Deletar a sessão completamente
        try {
          await supabase
            .from('smartfile_sessions')
            .delete()
            .eq('id', sessionId);
        } catch (sessionError) {
          console.error('Erro ao deletar sessão:', sessionError);
        }
        
        setUploadJobs(prevJobs => {
          const job = prevJobs.get(jobId);
          if (!job) return prevJobs;
          
          const failedJob = {
            ...job,
            status: 'failed' as const,
            progress: 100,
            stage: 'Documento não é laboratorial',
            error: 'O arquivo enviado não possui dados clínicos de saúde. Favor verificar.'
          };
          
          const newJobs = new Map(prevJobs);
          newJobs.set(jobId, failedJob);
          
          if (job.toastId) {
            toast.error(`❌ ${file.name} não possui dados clínicos de saúde`, {
              id: job.toastId,
              description: 'O arquivo enviado não possui dados clínicos de saúde. Favor verificar.',
              duration: 5000
            });
          }
          
          return newJobs;
        });
        
        // Limpar job após 5 segundos
        setTimeout(() => {
          setUploadJobs(prevJobs => {
            const newJobs = new Map(prevJobs);
            newJobs.delete(jobId);
            return newJobs;
          });
        }, 5000);
        
        return;
      }
      
      // Salvar registros automaticamente se houver structured_records
      if (session.structured_records && Array.isArray(session.structured_records) && session.structured_records.length > 0) {
        await saveStructuredRecords(patient.id, sessionId, session.structured_records);
        
        // Aguardar um pouco para o trigger SQL executar
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Finalizar sessão
        await finalizeSmartfileSession(sessionId);
        
        // Aguardar mais um pouco para garantir que os resultados foram criados
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Chamar linkByName para vincular resultados às subcategorias
        try {
          setUploadJobs(prevJobs => {
            const job = prevJobs.get(jobId);
            if (!job) return prevJobs;
            
            const updatedJob = {
              ...job,
              stage: 'Vinculando resultados...',
              progress: 95
            };
            
            const newJobs = new Map(prevJobs);
            newJobs.set(jobId, updatedJob);
            
            if (job.toastId) {
              toast.loading(`Processando ${file.name}...`, {
                id: job.toastId,
                description: 'Vinculando resultados às categorias...'
              });
            }
            
            return newJobs;
          });
          
          const linkResult = await linkByName.mutateAsync({ patientId: patient.id, mode: 'normalized' });
          
          // Só atualizar gráficos se houver resultados vinculados
          if (linkResult && (linkResult.updated > 0 || linkResult.total > 0)) {
            // Aguardar um pouco após vincular para garantir que os dados foram atualizados
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Atualizar dados após vincular resultados às categorias
            refetch();
            queryClient.invalidateQueries({ queryKey: ["lab-results", patient.id] });
            queryClient.invalidateQueries({ queryKey: ["lab-subcategories"] });
            
            // Disparar evento para atualizar gráficos após vincular (quando há resultados categorizados)
            window.dispatchEvent(new CustomEvent('reload-health-indicators'));
          }
        } catch (linkError) {
          console.error('[processFileInBackground] Erro ao vincular resultados:', linkError);
          // Não falhar o processo se o linkByName falhar
        }
      }
      
      // Marcar como concluído
      setUploadJobs(prevJobs => {
        const job = prevJobs.get(jobId);
        if (!job) return prevJobs;
        
        const completedJob = {
          ...job,
          status: 'completed' as const,
          progress: 100,
          stage: 'Concluído!'
        };
        
        const newJobs = new Map(prevJobs);
        newJobs.set(jobId, completedJob);
        
        if (job.toastId) {
          toast.success(`✅ ${file.name} processado e salvo!`, {
            id: job.toastId,
            description: `${session.structured_records?.length || 0} registro(s) salvo(s)`,
            duration: 5000
          });
        }
        
        return newJobs;
      });
      
      // Verificar se há resultados categorizados antes de atualizar gráficos
      const { data: categorizedResults } = await supabase
        .from("lab_results_onboarding")
        .select("id")
        .eq("patient_id", patient.id)
        .not("subcategory_id", "is", null)
        .limit(1);
      
      // Só atualizar gráficos se houver resultados categorizados
      if (categorizedResults && categorizedResults.length > 0) {
        // Aguardar um pouco para garantir que tudo foi processado
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Atualizar dados do paciente
        refetch();
        
        // Invalidar queries do React Query
        queryClient.invalidateQueries({ queryKey: ["lab-results", patient.id] });
        queryClient.invalidateQueries({ queryKey: ["patient", patient.id] });
        queryClient.invalidateQueries({ queryKey: ["lab-subcategories"] });
        
        // Disparar evento customizado para atualizar gráficos apenas após finalizar
        // Isso garante que os gráficos sejam atualizados quando há resultados categorizados
        window.dispatchEvent(new CustomEvent('reload-health-indicators'));
      } else {
        // Ainda assim atualizar dados básicos (sem gráficos)
        refetch();
      }
      
      // Limpar job após 5 segundos
      setTimeout(() => {
        setUploadJobs(prevJobs => {
          const newJobs = new Map(prevJobs);
          newJobs.delete(jobId);
          return newJobs;
        });
      }, 5000);
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Falha no processamento';
      console.error(`[processFileInBackground] Erro ao processar ${file.name}:`, error);
      
      setUploadJobs(prevJobs => {
        const job = prevJobs.get(jobId);
        if (!job) return prevJobs;
        
        const failedJob = {
          ...job,
          status: 'failed' as const,
          stage: `Erro: ${errorMessage}`,
          progress: 0
        };
        
        const newJobs = new Map(prevJobs);
        newJobs.set(jobId, failedJob);
        
        if (job.toastId) {
          toast.error(`❌ Erro ao processar ${file.name}`, {
            id: job.toastId,
            description: errorMessage,
            duration: 8000
          });
        }
        
        return newJobs;
      });
    }
  };

  const processText = async (text: string) => {
    if (!patient) return;

    setShowTextInput(false);
    setIsProcessing(true);
    setProgressStage("Iniciando...");
    setProgressValue(0);

    try {
      // Criar sessão smartfile vinculada ao paciente
      setProgressStage("Criando sessão...");
      setProgressValue(5);
      const sessionId = await createSmartfileSession(patient.id);
      setProgressValue(10);

      // Processar texto usando o mesmo fluxo do arquivo
      await processTextInputUltraFast(sessionId, text, (stage, prog) => {
        setProgressStage(stage);
        setProgressValue(prog);
      }, { skipAIInsights: isAIDisabled() });

      // ✅ VALIDAÇÃO: Verificar se o documento é "other" após processamento
      const session = await getSmartfileSession(sessionId);
      if (session.content_type === 'other') {
        // Deletar lab_results associados se existirem
        try {
          await supabase
            .from('lab_results_onboarding')
            .delete()
            .eq('source_session_id', sessionId);
        } catch (labError) {
          console.error('Erro ao deletar lab_results:', labError);
        }
        
        // Deletar a sessão completamente
        try {
          await supabase
            .from('smartfile_sessions')
            .delete()
            .eq('id', sessionId);
        } catch (sessionError) {
          console.error('Erro ao deletar sessão:', sessionError);
        }
        
        toast.error("O texto inserido não possui dados clínicos de saúde. Favor verificar.");
        setIsProcessing(false);
        setProgressStage("");
        setProgressValue(0);
        return;
      }

      setProgressValue(100);
      setProgressStage("Concluído!");
      toast.success("Texto processado com sucesso!");
      
      // Navegar para tela de análise inteligente (mesmo comportamento da página inicial)
      setTimeout(() => {
        navigate(`${basePath}/smartfile?sessionId=${sessionId}&patientId=${patient.id}`);
      }, 500);
    } catch (error) {
      console.error("Erro ao processar texto:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro ao processar texto";
      toast.error(errorMessage);
      setProgressStage("");
      setProgressValue(0);
    } finally {
      setIsProcessing(false);
    }
  };

  const { generateLink, isGenerating: isGeneratingLink } = useGenerateShareLink();
  const linkByName = useLinkByName();
  
  const [exportProgress, setExportProgress] = useState({
    isExporting: false,
    step: 0,
    message: '',
    totalSteps: 4
  });

  const [isExportRequested, setIsExportRequested] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  const exportSteps = [
    "Preparando dados do paciente...",
    "Renderizando gráficos e indicadores...",
    "Gerando PDF com alta qualidade...",
    "Finalizando e baixando arquivo..."
  ];

  const startPdfExport = async () => {
    if (!pdfContentRef.current) return;

    setExportProgress(prev => ({ ...prev, step: 3, message: exportSteps[2] }));

    try {
      const opt = {
        margin: [10, 10, 10, 10] as [number, number, number, number],
        filename: `relatorio-completo_${patient.name.replace(/\s+/g, '_')}_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: true,
          backgroundColor: '#ffffff'
        },
        jsPDF: { 
          unit: 'mm' as const, 
          format: 'a4' as const, 
          orientation: 'portrait' as const
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(pdfContentRef.current).save();

      // Passo 4: Finalizar
      setExportProgress(prev => ({ ...prev, step: 4, message: exportSteps[3], isExporting: false }));
      
      toast.success("✅ PDF gerado com sucesso!");
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Fechar modal de preview
      setShowPdfPreview(false);
    } catch (error) {
      console.error('[Export] Erro ao gerar PDF:', error);
      toast.error(`Erro ao gerar PDF: ${error instanceof Error ? error.message : "Erro desconhecido"}`);
      setExportProgress(prev => ({ ...prev, isExporting: false }));
      setShowPdfPreview(false);
    } finally {
      setIsExportRequested(false);
    }
  };

  const handleGenerateShareLink = async () => {
    if (!patient || !id) {
      toast.error("Dados do paciente não carregados");
      return;
    }

    try {
      const shareUrl = await generateLink(id, selectedBlocks);
      
      // Salvar o link gerado no estado
      setLastGeneratedShareUrl(shareUrl);
      
      // Copiar para clipboard
      await navigator.clipboard.writeText(shareUrl);
      
      // Criar mensagem WhatsApp com novo formato
      const whatsappMessage = encodeURIComponent(
        `*MiaSalute - Relatório de Saúde*\n\n` +
        `Olá, ${patient.name}.\n\n` +
        `Compartilho com você seu relatório de saúde.\n\n` +
        `Acesse aqui: ${shareUrl}\n\n` +
        `Este link expira em 24 horas.\n\n` +
        `Qualquer dúvida estou a disposição.\n\n` +
        `Atenciosamente,\n` +
        `${professionalProfile?.full_name || 'Seu profissional de saúde'}`
      );
      
      // Abrir WhatsApp
      window.open(`https://wa.me/?text=${whatsappMessage}`, '_blank');
      
      toast.success("Link gerado e copiado! Compartilhe via WhatsApp");
    } catch (error) {
      console.error(error);
    }
  };

  const handleAccessShareLink = async () => {
    if (!patient || !id) {
      toast.error("Dados do paciente não carregados");
      return;
    }

    try {
      let finalUrl: string | null = lastGeneratedShareUrl;

      // Se não há link em memória, buscar no banco
      if (!finalUrl) {
        // Buscar link existente
        const { data: existingLinks } = await supabase
          .from("shared_patient_links")
          .select("token, expires_at")
          .eq("patient_id", id)
          .eq("professional_id", (await supabase.auth.getUser()).data.user?.id)
          .order("created_at", { ascending: false })
          .limit(1);

        let token = existingLinks?.[0]?.token;
        
        // Verificar se link expirou
        if (token && existingLinks[0].expires_at) {
          const expiresAt = new Date(existingLinks[0].expires_at);
          if (expiresAt < new Date()) {
            token = null; // Link expirado, criar novo
          }
        }

        // Se não existe ou expirou, criar novo
        if (!token) {
          const shareUrl = await generateLink(id);
          setLastGeneratedShareUrl(shareUrl); // Salvar para próxima vez
          finalUrl = shareUrl;
        } else {
          // Construir URL a partir do token encontrado
          finalUrl = `${window.location.origin}/compartilhar/${token}`;
        }
      }

      // Abrir em nova aba usando a URL final
      if (finalUrl) {
        window.open(finalUrl, '_blank');
        toast.success("Abrindo página compartilhada...");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao acessar link compartilhado");
    }
  };

  const handleWhatsAppShare = async () => {
    if (!patient || !id) {
      toast.error("Dados do paciente não carregados");
      return;
    }

    setIsWhatsAppSharing(true);
    try {
      let finalUrl: string | null = lastGeneratedShareUrl;

      // Se não há link em memória, buscar no banco
      if (!finalUrl) {
        const { data: { user } } = await supabase.auth.getUser();
        
        const { data: existingLinks } = await supabase
          .from("shared_patient_links")
          .select("token, expires_at")
          .eq("patient_id", id)
          .eq("professional_id", user?.id)
          .order("created_at", { ascending: false })
          .limit(1);

        let token = existingLinks?.[0]?.token;
        
        // Verificar se link expirou
        if (token && existingLinks[0].expires_at) {
          const expiresAt = new Date(existingLinks[0].expires_at);
          if (expiresAt < new Date()) {
            token = null; // Link expirado
          }
        }

        // Se não existe ou expirou, gerar novo
        if (!token) {
          const shareUrl = await generateLink(id, selectedBlocks);
          setLastGeneratedShareUrl(shareUrl);
          finalUrl = shareUrl;
          toast.success("Novo link gerado!");
        } else {
          finalUrl = `${window.location.origin}/compartilhar/${token}`;
        }
      }

      // Criar mensagem WhatsApp (mesmo formato da função handleGenerateShareLink)
      const whatsappMessage = encodeURIComponent(
        `*MiaSalute - Relatório de Saúde*\n\n` +
        `Olá, ${patient.name}.\n\n` +
        `Compartilho com você seu relatório de saúde.\n\n` +
        `Acesse aqui: ${finalUrl}\n\n` +
        `Este link expira em 24 horas.\n\n` +
        `Qualquer dúvida estou a disposição.\n\n` +
        `Atenciosamente,\n` +
        `${professionalProfile?.full_name || 'Seu profissional de saúde'}`
      );
      
      // Abrir WhatsApp
      window.open(`https://wa.me/?text=${whatsappMessage}`, '_blank');
      
      toast.success("Abrindo WhatsApp...");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao compartilhar via WhatsApp");
    } finally {
      setIsWhatsAppSharing(false);
    }
  };

  const handleExportPDF = async () => {
    if (!patient || !id) {
      toast.error("Dados do paciente não carregados");
      return;
    }

    if (!patient?.name) {
      toast.error("Dados do paciente incompletos. Recarregue a página.");
      return;
    }

    // Abrir modal de preview
    setShowPdfPreview(true);

    setExportProgress({ 
      isExporting: true, 
      step: 1, 
      message: exportSteps[0], 
      totalSteps: 4 
    });

    // Passo 2: Marcar que queremos exportar
    setExportProgress(prev => ({ ...prev, step: 2, message: exportSteps[1] }));
    setIsExportRequested(true);
  };

  if (!id) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" onClick={() => navigate(`${basePath}/pacientes`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <div className="text-center py-12">
          <p className="text-lg text-muted-foreground">
            Carregando dados do paciente...
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6 w-full max-w-full overflow-x-hidden">
        {/* Header Skeleton */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
            <Skeleton className="h-10 w-10 rounded-md flex-shrink-0" />
            <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
              <Skeleton className="h-12 w-12 rounded-full flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
            </div>
          </div>
          <div className="border-t pt-3 pb-2">
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-9 w-24" />
              ))}
            </div>
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i}>
                    <CardContent className="pt-6">
                      <Skeleton className="h-12 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardHeader>
                      <Skeleton className="h-4 w-32" />
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <Skeleton className="h-48 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" onClick={() => navigate(`${basePath}/pacientes`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <div className="text-center py-12">
          <p className="text-lg text-muted-foreground">Paciente não encontrado</p>
        </div>
      </div>
    );
  }

  // Validar propriedades essenciais do paciente (CPF é opcional)
  if (!patient.id || !patient.name) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" onClick={() => navigate(`${basePath}/pacientes`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <div className="text-center py-12">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Erro nos dados do paciente</AlertTitle>
            <AlertDescription>
              Os dados do paciente estão incompletos. Por favor, tente recarregar a página.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <PatientDetailsErrorBoundary>
    <div className="space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Input oculto para upload de arquivo */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={currentPlan?.max_file_uploads === null || (currentPlan?.max_file_uploads ?? 1) > 1}
        accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.mp3,.wav,.m4a,.webm"
        onChange={handleFileSelected}
        className="hidden"
        disabled={isProcessing || Array.from(uploadJobs.values()).some(job => job.status === 'processing' || job.status === 'saving')}
      />

      <PatientPageHeader
        patient={patient}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onCameraClick={handleCameraClick}
        onAudioClick={handleAudioClick}
        onFileClick={handleFileClick}
        onTextClick={handleTextClick}
        isProcessing={isProcessing}
        progressStage={progressStage}
        progressValue={progressValue}
        captureOpen={captureSectionOpen}
        onCaptureOpenChange={setCaptureSectionOpen}
        basePath={basePath}
        availableTabs={availableTabs}
        showRecentApps={!isPatientArea}
      />

      {/* Lista de Jobs em Processamento - Upload Múltiplo */}
      {uploadJobs.size > 0 && (
        <Card className="border-primary/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">
                  Processando {Array.from(uploadJobs.values()).filter(j => j.status === 'processing' || j.status === 'saving' || j.status === 'pending').length} arquivo(s)
                </h3>
              </div>
              <div className="text-xs text-muted-foreground">
                {Array.from(uploadJobs.values()).filter(j => j.status === 'completed').length} concluído(s)
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {Array.from(uploadJobs.entries()).map(([jobId, job]) => {
              const isOtherDocument = job.status === 'failed' && job.error?.includes('não possui dados clínicos de saúde');
              
              return (
                <div 
                  key={jobId} 
                  className={`flex flex-col gap-2 p-3 rounded-lg ${
                    isOtherDocument 
                      ? 'bg-red-50 border-2 border-red-200 dark:bg-red-950/20 dark:border-red-800' 
                      : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      {job.status === 'processing' && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                      {job.status === 'saving' && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                      {job.status === 'completed' && <FileCheck className="h-4 w-4 text-green-600" />}
                      {job.status === 'failed' && <AlertCircle className="h-4 w-4 text-red-600" />}
                      {job.status === 'pending' && <Clock className="h-4 w-4 text-muted-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${isOtherDocument ? 'text-red-700 dark:text-red-300' : ''}`}>
                        {job.fileName}
                      </div>
                      <div className={`text-xs ${isOtherDocument ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                        {job.stage}
                      </div>
                      {(job.status === 'processing' || job.status === 'saving') && (
                        <Progress value={job.progress} className="h-1 mt-1" />
                      )}
                    </div>
                  </div>
                  {/* Descrição abaixo do processo para documentos "other" */}
                  {isOtherDocument && (
                    <div className="ml-7 text-xs text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded border border-red-200 dark:border-red-800">
                      O arquivo enviado não possui dados clínicos de saúde. Favor verificar.
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Conteúdo baseado na aba ativa */}
      <div className="w-full max-w-full overflow-x-hidden">
          {activeTab === "vision360" && patient && (
            <PatientVision360 
              patientId={patient.id} 
              patientSex={patient.gender}
            />
          )}

          {activeTab === "timeline" && patient && (
            <PatientTimeline
              patientId={patient.id}
              sessions={smartfileSessions || []}
              trainingPlans={trainingPlans || []}
              onRefresh={() => {
                void refetch();
              }}
            />
          )}

          {activeTab === "indicators" && (
            <div className="space-y-8">
              {/* Relatório Analítico em Dropdown no início */}
              <div id="relatorio-analitico" className="scroll-mt-6">
                <Accordion 
                  type="single" 
                  collapsible 
                  className="w-full"
                  value={accordionValue}
                  onValueChange={setAccordionValue}
                >
                  <AccordionItem value="relatorio-analitico" className="border-2 border-primary/10 rounded-xl overflow-hidden bg-gradient-to-br from-card to-card/50 shadow-lg">
                    <AccordionTrigger className="px-4 sm:px-6 py-4 hover:no-underline bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
                      <div className="flex items-center gap-3 flex-1 text-left">
                        <FileBarChart className="h-5 w-5 text-primary flex-shrink-0" />
                        <span className="text-base sm:text-lg font-semibold text-primary">
                          Relatório Analítico
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-0">
                      <div className="p-3 sm:p-6 w-full max-w-full overflow-x-hidden">
                        <LabResultsReport 
                          patientId={patient.id}
                          onBack={() => {}}
                        />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* Estatísticas em Dropdown */}
              <div id="estatisticas" className="scroll-mt-6">
                <Accordion 
                  type="single" 
                  collapsible 
                  className="w-full"
                  value={accordionValue}
                  onValueChange={setAccordionValue}
                >
                  <AccordionItem value="estatisticas" className="border-2 border-primary/10 rounded-xl overflow-hidden bg-gradient-to-br from-card to-card/50 shadow-lg">
                    <AccordionTrigger className="px-4 sm:px-6 py-4 hover:no-underline bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
                      <div className="flex items-center gap-3 flex-1 text-left">
                        <BarChart3 className="h-5 w-5 text-primary flex-shrink-0" />
                        <span className="text-base sm:text-lg font-semibold text-primary">
                          Estatísticas
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-0">
                      <div className="p-3 sm:p-6 w-full max-w-full overflow-x-hidden">
                        <PatientStatistics
                          totalContents={smartfileSessions.length}
                          totalRecords={smartfileSessions.length}
                          totalAlerts={alerts.length}
                          totalLabResults={labResults.length}
                          labResults={labResults}
                        />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              <PatientIndicadores 
                patientId={patient.id} 
                patientCpf={patient.cpf || ""}
              />
              
              {/* Análise Global IA - Exibida após os indicadores */}
              <GlobalAnalysisModern patientId={patient.id} />

              {!isPatientArea && (
                <>
                  {/* Seção Compartilhamento - Design Minimalista */}
                  <Card className="mt-8 bg-muted/30 border-border/50">
                    <div className="p-6">
                      {/* Título com linha separadora e botão configurar */}
                      <div className="flex items-center gap-4 mb-6">
                        <h3 className="text-lg font-semibold text-foreground">
                          Compartilhamento
                        </h3>
                        <div className="flex-1 h-px bg-border/50" />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowBlockSelector(!showBlockSelector)}
                          className="text-xs gap-1.5"
                        >
                          <Settings2 className="h-4 w-4" />
                          {showBlockSelector ? "Ocultar" : "Configurar"}
                        </Button>
                      </div>

                      {/* Seletor de blocos (colapsável) */}
                      {showBlockSelector && (
                        <div className="mb-6 p-4 bg-background rounded-lg border">
                          <ShareBlockSelector
                            selectedBlocks={selectedBlocks}
                            onChange={setSelectedBlocks}
                          />
                        </div>
                      )}

                      {/* Grid de botões minimalistas */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    
                    {/* Botão 1: Gerar Link */}
                    <Button
                      onClick={handleGenerateShareLink}
                      variant="ghost"
                      disabled={isGeneratingLink}
                      className="h-auto flex-col gap-3 py-6 hover:bg-primary/10 hover:scale-105 transition-all duration-200"
                    >
                      {isGeneratingLink ? (
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      ) : (
                        <Share2 className="h-8 w-8 text-primary" />
                      )}
                      <span className="text-sm font-medium text-muted-foreground">
                        {isGeneratingLink ? "Gerando..." : "Gerar Link"}
                      </span>
                    </Button>

                    {/* Botão 2: WhatsApp */}
                    <Button
                      onClick={handleWhatsAppShare}
                      variant="ghost"
                      disabled={isWhatsAppSharing || isGeneratingLink}
                      className="h-auto flex-col gap-3 py-6 hover:bg-[#25D366]/10 hover:scale-105 transition-all duration-200"
                    >
                      {isWhatsAppSharing ? (
                        <Loader2 className="h-8 w-8 animate-spin text-[#25D366]" />
                      ) : (
                        <svg
                          className="h-8 w-8 text-[#25D366]"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      )}
                      <span className="text-sm font-medium text-muted-foreground">
                        {isWhatsAppSharing ? "Enviando..." : "WhatsApp"}
                      </span>
                    </Button>

                    {/* Botão 3: Visualizar Link */}
                    <Button
                      onClick={handleAccessShareLink}
                      variant="ghost"
                      className="h-auto flex-col gap-3 py-6 hover:bg-green-500/10 hover:scale-105 transition-all duration-200"
                    >
                      <ExternalLink className="h-8 w-8 text-green-600" />
                      <span className="text-sm font-medium text-muted-foreground">
                        Visualizar
                      </span>
                    </Button>

                    {/* Botão 4: Exportar PDF */}
                    <Button
                      onClick={handleExportPDF}
                      variant="ghost"
                      className="h-auto flex-col gap-3 py-6 hover:bg-blue-500/10 hover:scale-105 transition-all duration-200"
                    >
                      <FileBarChart className="h-8 w-8 text-blue-600" />
                      <span className="text-sm font-medium text-muted-foreground">
                        PDF
                      </span>
                    </Button>

                      </div>
                    </div>
                  </Card>
                </>
              )}
            </div>
          )}

          {activeTab === "registros" && patient && (
            <PatientSmartfileRecords
              patientId={patient.id}
              sessions={smartfileSessions || []}
              onRefresh={refetch}
            />
          )}

          {activeTab === "insights" && (
            <div className="space-y-6">
              <GlobalAnalysisModern patientId={patient.id} />
            </div>
          )}

          {activeTab === "galeria" && patient && (
            <PatientGallery patientId={patient.id} />
          )}

          {activeTab === "coleta" && (
            <AssistedCollectionTab patientId={patient.id} />
          )}
        </div>


      {/* Dialogs */}
      {showUploadDialog && (
        <PatientDocumentUpload
          patientId={patient.id}
          onDocumentProcessed={(doc) => {
            setProcessedDocuments([doc]);
            setShowUploadDialog(false);
            setShowValidationDialog(true);
          }}
        />
      )}

      {showValidationDialog && processedDocuments.length > 0 && (
        <PatientDocumentValidation
          patientId={patient.id}
          documents={processedDocuments.map(doc => ({
            id: doc.documentId,
            fileName: doc.fileName,
            filePath: doc.filePath,
            extractedText: doc.extractedText,
            isEdited: false
          }))}
          onValidationComplete={() => {
            setShowValidationDialog(false);
            setProcessedDocuments([]);
            
            // ⭐ FEEDBACK VISUAL MELHORADO
            toast.info("⏳ Atualizando dados do paciente...", { 
              duration: 5000,
              id: 'patient-refresh' 
            });
            
            // ⭐ AUMENTAR TIMEOUT PARA 5 SEGUNDOS
            setTimeout(() => {
              refetch(); // Força reload completo de TODOS os dados
              toast.success("✅ Página atualizada com os novos registros!", { id: 'patient-refresh' });
              
              // ⭐ MUDAR PARA ABA DE REGISTROS AUTOMATICAMENTE
              setActiveTab('registros');
            }, 5000);
          }}
          onCancel={() => {
            setShowValidationDialog(false);
            setProcessedDocuments([]);
          }}
        />
      )}

      {showNoteDialog && (
        <PatientNoteInput
          patientId={patient.id}
          onNoteCreated={handleNoteCreated}
          onCancel={() => setShowNoteDialog(false)}
        />
      )}

      {showAnalysisModal && selectedDocumentId && (
        <DocumentAnalysisModal
          isOpen={showAnalysisModal}
          onClose={() => {
            setShowAnalysisModal(false);
            setSelectedDocumentId(null);
          }}
          documentId={selectedDocumentId}
          documentType={selectedDocumentType}
          patientId={patient.id}
        />
      )}

      {/* Overlays para câmera e áudio */}
      {showCamera && (
        <CameraCaptureMulti
          onCapture={handlePhotoCapture}
          onCancel={() => setShowCamera(false)}
          patientId={patient?.id || null}
        />
      )}

      {showAudio && (
        <AudioOverlay
          onComplete={handleAudioComplete}
          onClose={() => setShowAudio(false)}
          patient={patient ? {
            name: patient.name,
            birth_date: patient.birth_date,
            avatar_url: patient.avatar_url
          } : undefined}
        />
      )}

      {showTextInput && (
        <TextInputDialog
          onSubmit={processText}
          onClose={() => setShowTextInput(false)}
          patient={patient ? {
            name: patient.name,
            birth_date: patient.birth_date,
            avatar_url: patient.avatar_url
          } : undefined}
        />
      )}

      {/* Loader de exportação de PDF */}
      <PDFExportLoader 
        isVisible={exportProgress.isExporting}
        currentStep={exportProgress.step}
        totalSteps={exportProgress.totalSteps}
        message={exportProgress.message}
      />

      {/* Modal de Preview do PDF */}
      <Dialog open={showPdfPreview} onOpenChange={setShowPdfPreview}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-auto p-0">
          <div 
            ref={pdfContentRef}
            className="bg-white"
            style={{
              width: "210mm",
              minHeight: "297mm",
              margin: "0 auto",
              padding: "15mm"
            }}
          >
            {patient && id && (
              <PatientIndicadorsPDFContent
                patientId={id}
                patient={patient}
                onReady={() => {
                  if (isExportRequested) {
                    startPdfExport();
                  }
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        reason="file_uploads"
        requestedCount={requestedFileCount}
      />
    </div>
    </PatientDetailsErrorBoundary>
  );
}
